import type { FundraisingStatus } from "@entrilia/shared/api/fundraisingTypes";
import type { EntriliaProduct } from "@entrilia/shared/api/types";
import type { ApprovalItemType, WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import type { KnownCategoryTypes, PortalRole } from "../api/types/accessTypes";
import type { DataCollectionRequestAuditory, DataCollectionRequestStatus } from "../api/types/dataCollectionTypes";
import type { DataImportTarget } from "../api/types/dataImportTypes";
import type { InvestorPortalMessageSource } from "../api/types/documentCollectionTypes";
import type { EmailTrackingStatus } from "../api/types/emailsTypes";
import { ContactStatus } from "../api/types/fundStructureTypes";
import type { InboxChannelType } from "../api/types/inboxesTypes";
import type { InvestorCommunicationType } from "../api/types/investorCommunicationsTypes";
import type { ChecklistTaskStatus, EntityFieldType, ObjectFeature } from "../api/types/objectTypes";
import type { OrganizationContactRole } from "../api/types/userManagementTypes";

export const entriliaProductCaptionMap: Record<EntriliaProduct, string> = {
  EntriliaPlatform: "Entrilia Platform",
  OrganizationManagement: "Organization Management",
  CompanyManagement: "Settings",
  FundAccounting: "Fund Operations",
  CorporateAccounting: "Corporate Accounting",
  InvestorPortal: "Investor Portal",
  ReportingPortal: "SmartViews",
  AdminPortal: "Admin Portal",
  InvestorRelationsManagement: "Investor Relations",
  InvestorCommunications: "Investor Communications",
  DataRoom: "Data Room",
  ExpenseManagement: "Expense Management",
  PortfolioMonitoring: "Portfolio Monitoring",
  WorkflowManagement: "Workflow Management",
  DwhManagement: "Data Warehouse",
  BackupsManagement: "Backups",
  ApiGateway: "API Gateway",
  Dashboard: "Dashboards",
  Fundraising: "Fundraising",
  DataCollection: "Data Collection",
  Inbox: "Inbox",
  PortfolioPortal: "Portfolio Portal",
};

export const objectFeatureCaptionMap: Record<ObjectFeature, string> = {
  Checklists: "Checklists",
  Notes: "Notes",
  Files: "Files",
  PortfolioMetrics: "Portfolio Metrics",
  DataImport: "Data Import",
  Contacts: "Contact Permissions",
  InternalUserPermissions: "Internal User Permissions",
  CardFilePreview: "Card File Preview",
};

export const fieldTypeCaptionMap: Record<EntityFieldType, string> = {
  Text: "Text",
  Email: "Email",
  Phone: "Phone",
  Url: "URL",
  Number: "Number",
  Money: "Money",
  Percent: "Percent",
  Select: "Select",
  MultiSelect: "Multi-Select",
  UserDefinedOptionsSelect: "Select",
  UserDefinedOptionsMultiSelect: "Multi-Select",
  Date: "Date",
  DateTime: "Date & Time",
  Checkbox: "Checkbox",
  Lookup: "Lookup",
  MultiLookup: "Multi-Lookup",
  Guid: "GUID",
};

export const fundraisingStatusCaptionMap: Record<FundraisingStatus, string> = {
  Draft: "Draft",
  Preview: "Preview",
  Live: "Live",
  Completed: "Completed",
};

export const portalRoleCaptionMap: Record<PortalRole, string> = {
  LP_Admin: "Admin",
  LP_Member: "Member",
  Portfolio_Monitoring_Portal_Member: "Member",
};

export const dataImportTargetCaptionMap: Record<DataImportTarget, string> = {
  PlatformObjects: "Platform Objects",
  PortfolioMonitoringMetrics: "Metrics",
  PortfolioMonitoringMetricValues: "Metric Values",
  DocumentCollections: "Document Collections",
  Contacts: "Contacts",
  ContactAccess: "Contact access",
  Investors: "Investors",
  CommitmentDrafts: "Commitment drafts",
};

export const investorPortalMessageSourcesCaptionsMap: Record<InvestorPortalMessageSource, string> = {
  FundOpsDocumentCollection: "Fund Operations",
  DataImport: "Data Import",
};

export const dataCollectionRequestStatusCaptionsMap: Record<DataCollectionRequestStatus, string> = {
  Draft: "Draft",
  InProgress: "In Progress",
  Scheduled: "Scheduled",
  Completed: "Completed",
};

export const dataCollectionRequestAuditoryCaptionsMap: Record<DataCollectionRequestAuditory, string> = {
  PortfolioCompany: "Portfolio Company",
  Internal: "Internal",
};

export const contactStatusCaptionsMap: Map<ContactStatus, string> = new Map([
  [ContactStatus.NotRegistered, "Not Registered"],
  [ContactStatus.Registered, "Registered"],
]);

export const emailTrackingStatusCaptionsMap: Record<EmailTrackingStatus, string> = {
  ToBeSent: "To Be Sent",
  Sent: "Sent",
  PermanentFailure: "Bounced",
  Delivered: "Delivered",
  Opened: "Opened",
  Spam: "Spam",
  NotTracked: "Not Tracked",
};

export const workflowSignalTypeMap: Map<
  WorkflowSignalType,
  { caption: string; color: "primary" | "secondary" | "error" | "info" }
> = new Map([
  ["Approve", { caption: "Approve", color: "primary" }],
  ["Reject", { caption: "Reject", color: "error" }],
  ["Cancel", { caption: "Cancel", color: "secondary" }],
  ["CreateInvoiceTransaction", { caption: "Create Transaction", color: "primary" }],
  ["SubmitInvoiceWithoutConfiguredApproval", { caption: "Create Transaction", color: "primary" }],
  ["SubmitInvoiceForApproval", { caption: "Submit Invoice for Approval", color: "primary" }],
  ["BusinessCentralTransactionPosted", { caption: "Post Fund Accounting Transaction", color: "secondary" }],
  ["DeleteInvoice", { caption: "Delete Invoice", color: "error" }],
  ["ReturnInvoiceToDraft", { caption: "Return Invoice to Draft", color: "secondary" }],
]);

export const approvalItemTypeCaptionsMap: Record<ApprovalItemType, string> = {
  InvestorChangeRequest: "Investor Change Request",
  Invoice: "Invoice",
  ChecklistTask: "Checklist Task",
  MembersChangeRequest: "Contact Access Request",
};

export const inboxChannelTypeCaptionsMap: Record<InboxChannelType, string> = {
  Email: "Email",
  Api: "Manual",
};

export const categoryTypeCaptionsMap: Map<KnownCategoryTypes, string> = new Map([
  ["Message", "Investor Portal"],
  ["Fundraising", "Fundraising"],
  ["Data", "Data"],
]);

export const checklistTaskStatusCaptionsMap: Record<ChecklistTaskStatus, string> = {
  ToDo: "To Do",
  Done: "Done",
  Waived: "Waived",
  NotApplicable: "Not Applicable",
};

export const organizationContactRoleCaptionsMap: Record<OrganizationContactRole, string> = {
  PrimaryAccountContact: "Primary Account Contact",
  AccountManager: "Account Manager",
  BillingContact: "Billing Contact",
  SecurityContact: "Security Contact",
  TechnicalAdmin: "Technical Admin",
};

export const investorCommunicationTypeCaptionsMap: Record<InvestorCommunicationType, string> = {
  Article: "Article",
  Video: "Video",
};
